import React from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import DataTable from "@/components/DataTable";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line } from "recharts";
import { MEASURE_CAMPAIGNS, MEASURE_RATES_TIMELINE, formatEventType } from "@/data/mockData";
import { Send, MailOpen, MousePointerClick, CheckCircle2, Clock, DollarSign, ArrowUpRight, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

const CHANNEL_STYLES = {
  push: "border-primary/40 text-primary bg-primary/10",
  email: "border-[#4A88C7]/60 text-[#4A88C7] bg-[#4A88C7]/10",
  sms: "border-[#E9B44C]/60 text-[#B67F1B] dark:text-[#E9B44C] bg-[#E9B44C]/10",
  "in-app": "border-[#4B9F86]/60 text-[#4B9F86] bg-[#4B9F86]/10",
};

export default function Measure() {
  const totals = MEASURE_CAMPAIGNS.reduce(
    (acc, c) => ({
      sent: acc.sent + c.sent,
      delivered: acc.delivered + c.delivered,
      read: acc.read + c.read,
      acted: acc.acted + c.acted,
      converted: acc.converted + c.converted,
      revenue: acc.revenue + c.revenue,
    }),
    { sent: 0, delivered: 0, read: 0, acted: 0, converted: 0, revenue: 0 }
  );

  const pct = (a, b) => (b === 0 ? 0 : Math.round((a / b) * 100));

  return (
    <div className="p-6 lg:p-10 space-y-8">
      <div>
        <div className="label-caps mb-2">Post-action outcomes · last 30 days</div>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">Measure</h1>
        <p className="text-muted-foreground mt-2 text-sm max-w-2xl">
          For every action we sent, what actually happened next? Did people
          read the message, tap through, and complete the intended outcome —
          such as using the card they were reminded about?
        </p>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        <ImpactTile icon={Send} label="Actions sent" value={totals.sent.toLocaleString()} sub={`across ${MEASURE_CAMPAIGNS.length} campaigns`} testid="measure-sent" />
        <ImpactTile icon={MailOpen} label="Read rate" value={`${pct(totals.read, totals.delivered)}%`} sub={`${totals.read.toLocaleString()} messages read`} testid="measure-read" />
        <ImpactTile icon={MousePointerClick} label="Action rate" value={`${pct(totals.acted, totals.read)}%`} sub="tapped through after reading" testid="measure-acted" />
        <ImpactTile icon={CheckCircle2} label="Conversion" value={`${pct(totals.converted, totals.sent)}%`} sub="completed intended outcome" testid="measure-converted" highlight />
        <ImpactTile icon={DollarSign} label="Attributed revenue" value={`$${(totals.revenue / 1e6).toFixed(2)}M`} sub="from post-action conversions" testid="measure-revenue" highlight />
        <ImpactTile icon={Clock} label="Avg time-to-outcome" value="4.1d" sub="median post-send" testid="measure-time" />
        <ImpactTile icon={CheckCircle2} label="Best campaign" value="Card Activation" sub="21% conversion · $892K" testid="measure-best" />
        <ImpactTile icon={CheckCircle2} label="Worst campaign" value="Beneficiary Cross-sell" sub="20% conversion · $118K" testid="measure-worst" />
      </div>

      {/* Read vs Convert rate over time */}
      <Card className="p-6 border-border">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="label-caps mb-1.5">Trend</div>
            <div className="font-display text-2xl font-semibold">Read rate vs. Conversion rate — last 14 days</div>
          </div>
          <div className="flex gap-4 text-xs">
            <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-sm bg-primary" /> Read rate</span>
            <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-sm bg-[#E9B44C]" /> Conversion rate</span>
          </div>
        </div>
        <div className="h-72" data-testid="measure-rates-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={MEASURE_RATES_TIMELINE}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${v}%`} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }} formatter={(v) => `${v}%`} />
              <Line type="monotone" dataKey="read_rate" stroke="#124273" strokeWidth={2.5} dot={{ r: 3, fill: "#124273" }} />
              <Line type="monotone" dataKey="convert_rate" stroke="#E9B44C" strokeWidth={2.5} dot={{ r: 3, fill: "#E9B44C" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Campaign funnel */}
      <div>
        <div className="mb-3 flex items-end justify-between">
          <div>
            <div className="label-caps">Per action campaign</div>
            <div className="font-display text-2xl font-semibold">What happened after we sent the action</div>
          </div>
          <div className="text-xs text-muted-foreground">Reading from measurement pipeline · updated 4s ago</div>
        </div>

        <DataTable
          testIdPrefix="measure-table"
          initialPageSize={10}
          columns={[
            {
              key: "name", header: "Campaign",
              render: (r) => (
                <div>
                  <div className="font-medium text-sm">{r.name}</div>
                  <div className="text-[0.7rem] text-muted-foreground font-mono">via {r.workflow}</div>
                </div>
              ),
            },
            {
              key: "channel", header: "Channel",
              render: (r) => <Badge variant="outline" className={cn("uppercase text-[0.6rem]", CHANNEL_STYLES[r.channel])}>{r.channel}</Badge>,
            },
            {
              key: "trigger_event", header: "Trigger → Outcome",
              render: (r) => (
                <div className="text-[0.7rem] font-mono">
                  <div className="text-muted-foreground">{formatEventType(r.trigger_event)}</div>
                  <div className="text-primary flex items-center gap-1"><ArrowRight className="w-2.5 h-2.5" /> {formatEventType(r.desired_outcome)}</div>
                </div>
              ),
            },
            { key: "sent", header: "Sent", render: (r) => <span className="font-mono text-xs">{r.sent.toLocaleString()}</span> },
            { key: "read", header: "Read", render: (r) => <RateCell value={r.read} base={r.delivered} tone="#4A88C7" /> },
            { key: "acted", header: "Acted", render: (r) => <RateCell value={r.acted} base={r.read} tone="#E9B44C" /> },
            { key: "converted", header: "Converted", render: (r) => <RateCell value={r.converted} base={r.sent} tone="#4B9F86" /> },
            { key: "avg_time_to_convert_days", header: "Time-to-outcome", render: (r) => <span className="font-mono text-xs text-muted-foreground">{r.avg_time_to_convert_days}d</span> },
            { key: "revenue", header: "Revenue", render: (r) => <span className="font-mono text-primary font-semibold">${r.revenue.toLocaleString()}</span> },
          ]}
          data={MEASURE_CAMPAIGNS}
          searchable={["name", "workflow", "channel"]}
        />
      </div>
    </div>
  );
}

const ImpactTile = ({ icon: Icon, label, value, sub, testid, highlight }) => (
  <Card data-testid={testid} className={cn("p-5 border-border", highlight && "border-primary/40")}>
    <div className="flex items-start justify-between mb-4">
      <div className="label-caps">{label}</div>
      <div className={cn("w-8 h-8 rounded-md flex items-center justify-center", highlight ? "bg-primary/10 border border-primary/30 text-primary" : "bg-secondary text-muted-foreground")}>
        <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
      </div>
    </div>
    <div className="number-display text-3xl font-semibold">{value}</div>
    <div className="text-xs text-muted-foreground mt-2">{sub}</div>
  </Card>
);

const RateCell = ({ value, base, tone }) => {
  const p = base === 0 ? 0 : Math.round((value / base) * 100);
  return (
    <div className="flex items-center gap-2">
      <span className="font-mono text-xs w-12">{value.toLocaleString()}</span>
      <div className="flex-1 min-w-[50px] max-w-[80px] h-1.5 bg-secondary rounded-full overflow-hidden">
        <div className="h-full" style={{ width: `${p}%`, background: tone }} />
      </div>
      <Badge variant="outline" className="text-[0.6rem] font-mono">{p}%</Badge>
    </div>
  );
};
